/**
 * Init orchestration: config → state → modules → consent → identity →
 * lifecycle analytics.
 *
 * Lifecycle `/dye` events are direct `track()` calls at the point they occur;
 * the bus carries only cross-cutting signals (PAGE_VIEW for pixels,
 * CONSENT_CHANGED, IDENTITY_REFRESHED, INIT_ERROR).
 */

import { fetchWebsitePayload } from './config/client';
import { readSealedConfig } from './config/sealed';
import type { PoolWebsitePayload, SdkConfig } from './config/types';
import {
  mergePixels,
  normalizeSdkConfig,
  pixelsFromActiveAccount,
} from './config/normalize';
import {
  clearPersistentIds,
  readPersistentSessionId,
  resolveIdentity,
  type IdsContext,
  type SessionLifecycle,
} from './identity';
import { ConsentEngine } from './consent/engine';
import {
  awaitConsentSettled,
  destroyConsentGate,
  hasConsentGate,
  INERT_PRIVACY_API,
} from './consent/port';
import { discardKzqAdBuffer } from './kzq';
import { detectBotType } from './ua/bot';
import { parseUa } from './ua/parse';
import { setDebug, debug, warn } from '../util/log';
import { resolveOptions, type InitOptions, type ResolvedOptions } from './options';
import { resolvePageParams } from './analytics/pageParams';
import {
  resolveActiveAdAccount,
  resolveSourceMedium,
  sourceMediumPartsFromAccount,
} from './attribution/activeAdAccount';
import { track, trackBotDetected } from './analytics/track';
import { CORE_EVENTS } from './analytics/coreEvents';
import { startTimeOnSiteTracking } from './analytics/timeOnSite';
import { startLogoClickTracking } from './analytics/logoClick';
import { reportCpcValues } from './analytics/reportCpc';
import { emit, on, SdkEvent, type InitErrorPayload } from './events/bus';
import { AwaitFor, completeAwait, isAwaitCompleted } from './events/awaiter';
import { createSdkContext, type SdkContext } from './context';
import { setSdkState, getSdkStateOrNull, type SdkState } from './state';
import { resolveCountryFromUrl } from './queryParams';
import { maybeRedirectPreviewMissingDomain } from './previewRedirect';

export interface InitHooks {
  /**
   * Installs config-enabled modules (wired by `sdk.ts` to keep core
   * module-free). `phase: 'consent'` brings up the privacy module alone, so the
   * consent gate can answer before the rest of the SDK exists.
   */
  installModules(ctx: SdkContext, phase?: 'consent'): void;
  /**
   * Arms the pre-install senders as soon as options resolve (wired by `sdk.ts`).
   * Held back until consent when `requireConsent` is set — it reports.
   */
  onOptionsResolved?(options: ResolvedOptions): void;
  sdkVersion: string;
}

let initStarted = false;
let moduleContext: SdkContext | null = null;

export function isInitStarted(): boolean {
  return initStarted;
}

export function getSdkContext(): SdkContext {
  if (!moduleContext) {
    throw new Error('[kueez-sdk] init() must complete before using the SDK context');
  }
  return moduleContext;
}

export function init(opts: InitOptions, hooks: InitHooks): void {
  if (initStarted) {
    warn('init() called twice — ignoring');
    return;
  }
  // preview.kueez.website is not a Pool website — require kz_domain before init.
  if (maybeRedirectPreviewMissingDomain()) return;

  initStarted = true;

  void runInit(opts, hooks).catch((err) => {
    emit(SdkEvent.INIT_ERROR, { error: err as Error, phase: 'unknown' });
  });
}

async function runInit(initArgs: InitOptions, hooks: InitHooks): Promise<void> {
  const options = resolveOptions(initArgs);
  setDebug(options.debug);
  debug('init starting', { domain: options.domain, apiBaseUrl: options.apiBaseUrl });

  let earlySendersArmed = false;
  const armEarlySenders = (): void => {
    if (earlySendersArmed) return;
    earlySendersArmed = true;
    try {
      hooks.onOptionsResolved?.(options);
    } catch {
      /* never blocks init */
    }
  };
  // The pre-install kzq sender reports on pagehide, so a build that must not run
  // without consent cannot arm it before the gate answers.
  if (!options.requireConsent) armEarlySenders();

  let modulesInstalled = false;
  let haltedForConsent = false;

  try {
    let websitePayload: PoolWebsitePayload | null = null;
    let config: SdkConfig;

    // Branches on the build constant, not on the config value: esbuild folds
    // this away, which is what keeps the pool client out of sealed bundles.
    if (__SEALED__) {
      const sealed = readSealedConfig();
      if (!sealed) throw new Error('sealed build has no config baked in');
      debug('using sealed config — no pool fetch', { domain: sealed.website.domain });
      config = sealed;
    } else {
      const countryOverride = resolveCountryFromUrl() || undefined;
      if (countryOverride) {
        debug('passing kz_cc country override to pool', { country: countryOverride });
      }
      websitePayload = await fetchWebsitePayload({
        domain: options.domain,
        apiBaseUrl: options.apiBaseUrl,
        country: countryOverride,
      });
      config = normalizeSdkConfig(websitePayload, {
        pixels: options.pixels,
      });
    }
    if (options.activityId) config.analytics.activityId = options.activityId;
    if (options.activityModel) config.analytics.activityModel = options.activityModel;
    if (options.ecomAccountId) config.ecom.ecomAccountId = options.ecomAccountId;
    if (options.trackers.length > 0) config.analytics.trackers = [...options.trackers];

    const pageParams = resolvePageParams();
    const sourceMedium = resolveSourceMedium({
      initSourceMedium: options.sourceMedium,
      pageParams,
    });
    const activeAdAccount = resolveActiveAdAccount(config.adAccounts, sourceMedium);
    config.pixels = mergePixels(
      pixelsFromActiveAccount(activeAdAccount),
      options.pixels,
    );

    if (activeAdAccount) {
      const { source, medium } = sourceMediumPartsFromAccount(activeAdAccount);
      debug('active ad account matched', {
        platform: activeAdAccount.platform,
        source,
        medium,
        mbid: pageParams.mediaBuyingId || undefined,
        sourceMedium: activeAdAccount.sourceMedium,
      });
    } else {
      debug('active ad account unmatched', {
        sourceMedium,
        adAccountCount: config.adAccounts.length,
      });
    }

    debug('config normalized', {
      website: {
        id: config.website.id,
        domain: config.website.domain,
        rootDomain: config.website.root_domain,
      },
      ecom: config.ecom,
      pixels: config.pixels.map((pixel) => ({
        platform: pixel.platform,
        pixelId: pixel.pixel_id,
      })),
      adAccounts: config.adAccounts.map((account) => ({
        platform: account.platform,
        sourceMedium: account.sourceMedium,
        hasPixel: Boolean(account.pixel),
      })),
      trackers: config.analytics.trackers,
      cmp: config.cmp,
      pageParams: {
        utmSource: pageParams.utmSource,
        utmMedium: pageParams.utmMedium,
        mbid: pageParams.mediaBuyingId,
      },
    });

    completeAwait(AwaitFor.CONFIG, config);

    const idsCtx: IdsContext = { rootDomain: config.website.root_domain };
    const hadPersistentSessionAtEntry = readPersistentSessionId() !== null;

    const state: SdkState = {
      options,
      config,
      websitePayload,
      ua: parseUa(),
      pageParams,
      activeAdAccount,
      engine: new ConsentEngine(),
      privacy: INERT_PRIVACY_API,
      identity: { current: null },
      contentFetch: { inFlight: 0, lastValue: null },
      content: { locale: null },
    };
    setSdkState(state);
    moduleContext = createSdkContext(hooks.sdkVersion);
    const ctx = moduleContext;

    const watchConsentFlips = (): void => {
      on(SdkEvent.CONSENT_CHANGED, ({ status, prev }) => {
        if (status === 'denied') clearPersistentIds(idsCtx);
        const resolved = resolveIdentity(idsCtx, status === 'granted');
        state.identity.current = resolved.ids;
        emit(SdkEvent.IDENTITY_REFRESHED, resolved.ids);
        if (prev !== 'granted' && status === 'granted' && isAwaitCompleted(AwaitFor.READY)) {
          debug('consent flipped to granted post-ready → re-firing page view');
          dispatchPageView();
        }
      });
    };

    /** Identity, bot classification and the lifecycle events. */
    const runLifecycle = (): void => {
      const initGranted = state.engine.getStatus() === 'granted';
      const initResolved = resolveIdentity(idsCtx, initGranted);
      state.identity.current = initResolved.ids;

      if (!isAwaitCompleted(AwaitFor.IDENTITY)) {
        completeAwait(AwaitFor.IDENTITY, initResolved.ids);
      }

      // Full yoto detectBot() — country is available now. `isbt` bypasses the
      // track() bot gate so Detected Bots lands in BI; lifecycle events stay dropped.
      const botType = detectBotType({ countryCode: config.geo.country });
      if (botType) {
        trackBotDetected(botType);
        debug('bot traffic — reported isbt, skipping lifecycle', { botType });
      } else {
        if (initResolved.userLifecycle === 'init') {
          track(CORE_EVENTS.userInit);
        } else if (initResolved.userLifecycle === 'existing') {
          track(CORE_EVENTS.userExisting);
        }
        const sessionLifecycle: SessionLifecycle = hadPersistentSessionAtEntry
          ? initResolved.sessionLifecycle
          : 'init';
        track(sessionLifecycle === 'init' ? CORE_EVENTS.sessionInit : CORE_EVENTS.sessionRefresh);
        dispatchPageView();
        reportCpcValues(pageParams, activeAdAccount);
      }

      completeAwait(AwaitFor.READY);

      if (options.onReady) {
        try {
          options.onReady();
        } catch (err) {
          warn('onReady threw', err);
        }
      }
      debug('init done', { consent: state.engine.getStatus() });
    };

    if (options.requireConsent) {
      // Consent decides whether the SDK runs at all, so it is resolved before
      // anything else: the privacy module comes up alone, and nothing installs,
      // reports or stores until it answers.
      hooks.installModules(ctx, 'consent');
      await awaitConsentSettled();

      const startSdk = (): void => {
        armEarlySenders();
        hooks.installModules(ctx);
        modulesInstalled = true;
        track(CORE_EVENTS.sdkInit, { pkgv: hooks.sdkVersion });
        watchConsentFlips();
        runLifecycle();
      };

      if (hasConsentGate() && state.engine.getStatus() !== 'granted') {
        // READY stays pending too: resolving it would tell the host the SDK is
        // up, and host code acting on that could feed track() while we must be
        // silent. runLifecycle completes it if consent arrives.
        haltedForConsent = true;
        haltUntilConsent(startSdk);
        return;
      }

      startSdk();
      return;
    }

    hooks.installModules(ctx);
    modulesInstalled = true;

    track(CORE_EVENTS.sdkInit, { pkgv: hooks.sdkVersion });

    watchConsentFlips();

    await awaitConsentSettled();

    runLifecycle();
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err));
    const phase: InitErrorPayload['phase'] = !isAwaitCompleted(AwaitFor.CONFIG)
      ? 'config'
      : 'consent';
    emit(SdkEvent.INIT_ERROR, { error, phase });
    warn('init failed — unblocking READY', error);
    // requireConsent fails closed: an error before the gate answered must not
    // bring up modules that observe and report.
    if (!options.requireConsent && !modulesInstalled && getSdkStateOrNull() && moduleContext) {
      try {
        hooks.installModules(moduleContext);
      } catch {
        /* context may be incomplete */
      }
    }
  } finally {
    if (!haltedForConsent && !isAwaitCompleted(AwaitFor.READY)) {
      completeAwait(AwaitFor.READY);
    }
  }
}

/**
 * No consent, no SDK: nothing installs, nothing is reported, nothing is stored,
 * and the ad events the publisher stub buffered are dropped with the rest. The
 * privacy module keeps listening to the page's CMP, so a grant that arrives
 * later starts the SDK from here.
 */
function haltUntilConsent(start: () => void): void {
  debug('requireConsent: page did not grant consent — init halted');
  discardKzqAdBuffer();
  const off = on(SdkEvent.CONSENT_CHANGED, ({ status }) => {
    if (status !== 'granted') return;
    off();
    debug('consent granted after the halt — starting the SDK');
    start();
  });
}

/** `/dye` analytics:pageview + compact pv, then the PAGE_VIEW bus signal (pixels listen). */
function dispatchPageView(): void {
  track(CORE_EVENTS.pageView);
  track(CORE_EVENTS.pageViewCompact);
  startTimeOnSiteTracking(getSdkStateOrNull()?.options.activeTimeOnly === true);
  startLogoClickTracking();
  emit(SdkEvent.PAGE_VIEW);
}

/** Test-only — allow a fresh init cycle. */
export function _resetInitForTests(): void {
  destroyConsentGate();
  initStarted = false;
  moduleContext = null;
}
